import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useUserAuth } from "@/user/UserAuthContext";

/**
 * Favorites (saved items) state container.
 *
 * Responsibilities:
 *  - On sign-in, fetch the user's saved tool/article slugs once via
 *    `GET /api/favorites/ids`. Stored as two `Set<slug>` so card-level
 *    `isFavorited()` lookups stay O(1).
 *  - Expose `toggle(itemType, itemSlug)` with **optimistic updates**:
 *    flip the local Set immediately, fire the network call in the
 *    background, revert on failure. Cards therefore feel instant.
 *  - On sign-out, clear the local sets so the previous user's saved
 *    items don't leak across sessions on the same device.
 *
 * Auth-gated calls: every endpoint requires a logged-in session. If a
 * caller invokes `toggle()` while signed-out, the function returns a
 * `{ ok: false, reason: "unauthenticated" }` shape so the UI can
 * trigger the sign-in flow without itself looking at user state.
 */

const FavoritesContext = createContext(null);

const TYPES = ["tool", "article"];

function emptyState() {
  return { tool: new Set(), article: new Set() };
}

export function FavoritesProvider({ children }) {
  const { user, status } = useUserAuth();
  // `sets[type]` is a Set<slug>. We store one Set per item type so a
  // re-render only triggers when the relevant type's Set identity
  // changes (we always replace, never mutate).
  const [sets, setSets] = useState(() => emptyState());
  const [loading, setLoading] = useState(false);

  // ---- Sync remote → local once per signed-in session ----------------
  useEffect(() => {
    if (status !== "authenticated" || !user) {
      setSets(emptyState());
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`/api/favorites/ids`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`ids:${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setSets({
          tool: new Set(data.tools || []),
          article: new Set(data.articles || []),
        });
      } catch {
        // Network blip — leave local state empty; per-card toggles still
        // work and will retry on next render.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, user?.user_id]);

  const isFavorited = useCallback(
    (itemType, itemSlug) => {
      if (!TYPES.includes(itemType) || !itemSlug) return false;
      return sets[itemType]?.has(itemSlug) || false;
    },
    [sets]
  );

  /**
   * Optimistic toggle. Returns one of:
   *   { ok: true, favorited: bool }      — local flip + remote synced
   *   { ok: false, reason: "unauthenticated" }  — caller should kick off
   *                                                sign-in flow
   *   { ok: false, reason: "network" }   — local state is reverted
   */
  const toggle = useCallback(
    async (itemType, itemSlug, itemId = undefined) => {
      if (!TYPES.includes(itemType) || !itemSlug) {
        return { ok: false, reason: "invalid" };
      }
      if (status !== "authenticated" || !user) {
        return { ok: false, reason: "unauthenticated" };
      }
      const wasFavorited = sets[itemType]?.has(itemSlug) || false;
      const optimistic = new Set(sets[itemType] || []);
      if (wasFavorited) optimistic.delete(itemSlug);
      else optimistic.add(itemSlug);
      setSets((prev) => ({ ...prev, [itemType]: optimistic }));

      try {
        const res = await fetch(`/api/favorites/toggle`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            item_type: itemType,
            item_slug: itemSlug,
            item_id: itemId,
          }),
        });
        if (!res.ok) throw new Error(`toggle:${res.status}`);
        const data = await res.json();
        // Reconcile with the authoritative server response (handles the
        // edge case where local + server disagree, e.g. two tabs racing).
        setSets((prev) => {
          const next = new Set(prev[itemType] || []);
          if (data.favorited) next.add(itemSlug);
          else next.delete(itemSlug);
          return { ...prev, [itemType]: next };
        });
        return { ok: true, favorited: !!data.favorited };
      } catch {
        // Revert optimistic update.
        setSets((prev) => {
          const next = new Set(prev[itemType] || []);
          if (wasFavorited) next.add(itemSlug);
          else next.delete(itemSlug);
          return { ...prev, [itemType]: next };
        });
        return { ok: false, reason: "network" };
      }
    },
    [status, user, sets]
  );

  /**
   * Pull the full saved-items list (with timestamps + ids) for the
   * /account "Saved Items" page. Optimised so the account page can
   * call this once and render both tabs.
   */
  const fetchList = useCallback(async () => {
    if (status !== "authenticated") {
      return { tools: [], articles: [] };
    }
    try {
      const res = await fetch(`/api/favorites`, { credentials: "include" });
      if (!res.ok) throw new Error(`list:${res.status}`);
      return await res.json();
    } catch {
      return { tools: [], articles: [] };
    }
  }, [status]);

  const value = useMemo(
    () => ({
      isFavorited,
      toggle,
      fetchList,
      loading,
      counts: {
        tool: sets.tool?.size || 0,
        article: sets.article?.size || 0,
      },
    }),
    [isFavorited, toggle, fetchList, loading, sets]
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    // Defensive default so any component that mounts before the provider
    // (e.g. during early hydration) gets a usable shape.
    return {
      isFavorited: () => false,
      toggle: async () => ({ ok: false, reason: "unauthenticated" }),
      fetchList: async () => ({ tools: [], articles: [] }),
      loading: false,
      counts: { tool: 0, article: 0 },
    };
  }
  return ctx;
}
